import { useQuery } from "@tanstack/react-query";
import { Archive, ChevronRight, FileCode, FileDown } from "lucide-react";
import { useState } from "react";

import {
  descargarFacturaCompraArchivo,
  descargarFacturasCompraZip,
  getFacturaCompraConceptos,
  getFacturasCompra,
  type FacturaCompraItem,
} from "@/lib/api";

// OJO: los NUMERIC de Postgres llegan como string en el JSON (Decimal→str);
// siempre convertir con Number() antes de sumar o formatear.
const money = (n: number | string | null, moneda: string | null = "MXN") =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: moneda === "USD" ? "USD" : "MXN",
      }).format(Number(n));
const num = (n: number | string | null, d = 2) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(Number(n));
const fechaCorta = (s: string | null) => (s ? s.slice(0, 10) : "—");

// Filtro por defecto: los últimos 3 meses de periodo_ecd.
function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function hace3MesesIso(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export function FacturasCompraPage() {
  const [desde, setDesde] = useState<string>(hace3MesesIso());
  const [hasta, setHasta] = useState<string>(hoyIso());
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["facturas-compra", desde, hasta],
    queryFn: () => getFacturasCompra(desde || undefined, hasta || undefined),
  });
  const facturas = q.data?.items ?? [];
  const sumaTotal = facturas.reduce((s, f) => s + (Number(f.total) || 0), 0);

  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const toggle = (fuf: string) =>
    setAbiertas((prev) => {
      const n = new Set(prev);
      if (n.has(fuf)) n.delete(fuf);
      else n.add(fuf);
      return n;
    });

  // Selección para el ZIP (por FUF).
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [zipBusy, setZipBusy] = useState(false);
  const toggleSel = (fuf: string) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(fuf)) n.delete(fuf);
      else n.add(fuf);
      return n;
    });
  const todasSeleccionadas = facturas.length > 0 && facturas.every((f) => sel.has(f.fuf));
  const toggleSelTodas = () =>
    setSel(todasSeleccionadas ? new Set() : new Set(facturas.map((f) => f.fuf)));

  const descargarZip = async () => {
    setError(null);
    setZipBusy(true);
    try {
      await descargarFacturasCompraZip([...sel]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al generar el ZIP");
    } finally {
      setZipBusy(false);
    }
  };

  const descargar = async (fuf: string, tipo: "pdf" | "xml") => {
    setError(null);
    try {
      await descargarFacturaCompraArchivo(fuf, tipo);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Error al descargar el ${tipo.toUpperCase()}`);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Facturas de Compra CENACE</h1>
        <p className="text-sm text-slate-500 mt-1">
          CFDI que CENACE emite a MPP (descargados del MEMSIM por el ETL). Toca una factura
          para ver sus conceptos y descarga el PDF o el XML.
        </p>
      </div>

      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Periodo ECD desde</span>
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">Periodo ECD hasta</span>
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setDesde("");
            setHasta("");
          }}
          className="text-sm text-slate-500 hover:text-slate-900 border rounded px-3 py-1.5"
        >
          Limpiar (ver todas)
        </button>
        <button
          type="button"
          disabled={sel.size === 0 || zipBusy}
          onClick={descargarZip}
          title={
            sel.size === 0
              ? "Selecciona facturas con las casillas de la tabla"
              : `Descargar ZIP con PDF+XML de ${sel.size} factura${sel.size === 1 ? "" : "s"}`
          }
          className="inline-flex items-center gap-1.5 text-sm border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Archive className="w-4 h-4" />
          {zipBusy ? "Generando ZIP…" : `Descargar ZIP${sel.size > 0 ? ` (${sel.size})` : ""}`}
        </button>
        {!q.isLoading && (
          <div className="ml-auto text-sm text-slate-500">
            {facturas.length} factura{facturas.length === 1 ? "" : "s"} · Total{" "}
            <b className="tabular-nums text-slate-700">{money(sumaTotal)}</b>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudieron cargar las facturas: {(q.error as Error).message}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        {q.isLoading ? (
          <div className="p-4 text-sm text-slate-500">Cargando…</div>
        ) : facturas.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            No hay facturas de compra con periodo ECD en el rango seleccionado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="px-3 py-2 w-8 text-center">
                    <input
                      type="checkbox"
                      checked={todasSeleccionadas}
                      onChange={toggleSelTodas}
                      title={todasSeleccionadas ? "Quitar selección" : "Seleccionar todas"}
                      className="w-3.5 h-3.5 accent-blue-600 cursor-pointer align-middle"
                    />
                  </th>
                  <th className="px-3 py-2 w-8" />
                  <th className="px-3 py-2">Periodo ECD</th>
                  <th className="px-3 py-2">FUF</th>
                  <th className="px-3 py-2">Serie-Folio</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Sistema</th>
                  <th className="px-3 py-2">Estatus</th>
                  <th className="px-3 py-2">Límite de pago</th>
                  <th className="px-3 py-2 text-right">Subtotal</th>
                  <th className="px-3 py-2 text-right">IVA</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Descargas</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((f) => (
                  <FilaFactura
                    key={f.fuf}
                    f={f}
                    abierta={abiertas.has(f.fuf)}
                    seleccionada={sel.has(f.fuf)}
                    onToggle={() => toggle(f.fuf)}
                    onSeleccionar={() => toggleSel(f.fuf)}
                    onDescargar={descargar}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FilaFactura({
  f,
  abierta,
  seleccionada,
  onToggle,
  onSeleccionar,
  onDescargar,
}: {
  f: FacturaCompraItem;
  abierta: boolean;
  seleccionada: boolean;
  onToggle: () => void;
  onSeleccionar: () => void;
  onDescargar: (fuf: string, tipo: "pdf" | "xml") => void;
}) {
  return (
    <>
      <tr
        className={`border-b last:border-0 hover:bg-slate-50 cursor-pointer ${seleccionada ? "bg-blue-50/50" : ""}`}
        onClick={onToggle}
      >
        <td
          className="px-3 py-2 text-center"
          onClick={(e) => e.stopPropagation() /* que seleccionar no abra/cierre la fila */}
        >
          <input
            type="checkbox"
            checked={seleccionada}
            onChange={onSeleccionar}
            title="Incluir esta factura en el ZIP"
            className="w-3.5 h-3.5 accent-blue-600 cursor-pointer align-middle"
          />
        </td>
        <td className="px-3 py-2 text-slate-400">
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform ${abierta ? "rotate-90" : ""}`}
          />
        </td>
        <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(f.periodo_ecd)}</td>
        <td className="px-3 py-2 font-medium whitespace-nowrap">{f.fuf}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          {[f.serie, f.folio].filter(Boolean).join("-") || "—"}
        </td>
        <td className="px-3 py-2">{f.tipo_doc ?? "—"}</td>
        <td className="px-3 py-2">{f.sistema ?? "—"}</td>
        <td className="px-3 py-2">{f.estatus ?? "—"}</td>
        <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(f.fecha_lim_pago)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{money(f.subtotal, f.moneda)}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {money(f.total_impuestos_trasladados, f.moneda)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">
          {money(f.total, f.moneda)}
        </td>
        <td className="px-3 py-2">
          <div
            className="flex justify-end gap-2"
            onClick={(e) => e.stopPropagation() /* que descargar no abra/cierre la fila */}
          >
            <button
              type="button"
              disabled={!f.tiene_pdf}
              title={f.tiene_pdf ? "Descargar PDF" : "PDF no disponible"}
              onClick={() => onDescargar(f.fuf, "pdf")}
              className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileDown className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              type="button"
              disabled={!f.tiene_xml}
              title={f.tiene_xml ? "Descargar XML" : "XML no disponible"}
              onClick={() => onDescargar(f.fuf, "xml")}
              className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileCode className="w-3.5 h-3.5" /> XML
            </button>
          </div>
        </td>
      </tr>
      {abierta && (
        <tr className="bg-slate-50/60 border-b last:border-0">
          <td colSpan={13} className="px-6 py-3">
            <DetalleFactura f={f} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetalleFactura({ f }: { f: FacturaCompraItem }) {
  const q = useQuery({
    queryKey: ["factura-compra-conceptos", f.fuf],
    queryFn: () => getFacturaCompraConceptos(f.fuf),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-3 max-w-5xl">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0.5 text-xs">
        <Linea k="UUID (timbre fiscal)" v={f.uuid} />
        <Linea k="Emisor" v={f.emisor_nombre && `${f.emisor_nombre} (${f.emisor_rfc ?? ""})`} />
        <Linea k="Receptor" v={f.receptor_nombre && `${f.receptor_nombre} (${f.receptor_rfc ?? ""})`} />
        <Linea k="Fecha del CFDI" v={f.fecha?.replace("T", " ")} />
        <Linea k="Fecha de publicación" v={f.fecha_publicacion?.replace("T", " ")} />
        <Linea k="Participante CENACE" v={f.id_partic_cenace} />
        <Linea k="Banco" v={f.banco} />
        <Linea k="Cuenta / CLABE" v={[f.cuenta, f.clabe].filter(Boolean).join(" / ")} />
        <Linea k="Referencia bancaria" v={f.ref_banco} />
        <Linea k="Cuenta de orden" v={f.cuenta_orden} />
        <Linea k="Archivo" v={f.nombre_archivo} />
      </div>
      {f.monto_letra && <div className="text-xs text-slate-500 italic">{f.monto_letra}</div>}

      <div>
        <div className="text-xs font-medium text-slate-600 mb-1">Conceptos (addenda CENACE)</div>
        {q.isLoading && <div className="text-xs text-slate-400">Cargando conceptos…</div>}
        {q.isError && (
          <div className="text-xs text-red-600">
            No se pudieron cargar los conceptos: {(q.error as Error).message}
          </div>
        )}
        {q.data &&
          (q.data.length === 0 ? (
            <div className="text-xs text-slate-400">Esta factura no tiene conceptos cargados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-xs border rounded bg-white min-w-[36rem]">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="px-2.5 py-1">#</th>
                    <th className="px-2.5 py-1">Folio único</th>
                    <th className="px-2.5 py-1">Concepto</th>
                    <th className="px-2.5 py-1 text-right">Cantidad</th>
                    <th className="px-2.5 py-1">Unidad</th>
                    <th className="px-2.5 py-1 text-right">P. unitario</th>
                    <th className="px-2.5 py-1 text-right">Importe</th>
                    <th className="px-2.5 py-1 text-right">IVA</th>
                    <th className="px-2.5 py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.map((c) => (
                    <tr key={c.num_lin} className="border-b last:border-0">
                      <td className="px-2.5 py-1 text-slate-500">{c.num_lin}</td>
                      <td className="px-2.5 py-1 whitespace-nowrap">{c.folio_unico ?? "—"}</td>
                      <td className="px-2.5 py-1">{c.concepto ?? "—"}</td>
                      <td className="px-2.5 py-1 text-right tabular-nums">{num(c.cantidad, 3)}</td>
                      <td className="px-2.5 py-1">{c.unidad ?? "—"}</td>
                      <td className="px-2.5 py-1 text-right tabular-nums">{num(c.precio_unit, 4)}</td>
                      <td className="px-2.5 py-1 text-right tabular-nums">{money(c.importe_linea, f.moneda)}</td>
                      <td className="px-2.5 py-1 text-right tabular-nums">{money(c.iva, f.moneda)}</td>
                      <td className="px-2.5 py-1 text-right tabular-nums font-medium">{money(c.total, f.moneda)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </div>
    </div>
  );
}

function Linea({ k, v }: { k: string; v: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-0.5">
      <span className="text-slate-500 whitespace-nowrap">{k}</span>
      <span className="font-medium text-slate-700 text-right break-all">{v || "—"}</span>
    </div>
  );
}
